import cx from 'classnames';
import * as _ from 'lodash';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { BINARY_DECOMPOSITION } from '../../../../../shared/featureFlags/flagNames';
import { BillingTermType } from '../../../../../shared/model-typings';
import { PREMIUM_PER_REVISION_PLAN_NAME } from '../../../../../shared/subscriptions';
import { window } from '../../../../globals';
import { errorMessageFromResponse } from '../../../../helpers/errors';
import { useIsFeatureEnabled } from '../../../../helpers/features/featureFlags';
import { useAddBillingTerm } from '../../../../hooks/api/billing/useAddBillingTerm';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import LoaderButton from '../../../generic/Button/LoaderButton';
import LabeledNumberInput from '../../../generic/Forms/LabeledNumberInput';
import LabeledSelect from '../../../generic/Forms/LabeledSelect';
import LabeledTextInput from '../../../generic/Forms/LabeledTextInput';
import Text from '../../../generic/Text';
import type { SelectOption } from '../../../../types';

import styles from './BillingTermCard.module.less';

const revisionOption = { label: 'Revision', value: BillingTermType.Revision };
const binaryOption = { label: 'Binary', value: BillingTermType.Binary };
const options = [revisionOption, binaryOption];

/**
 * Validates and returns a date object from a string
 * @param dateInput {string} possible date input
 * @returns {Date | null} date
 */
const getAndValidateDate = (dateInput: string): Date | null => {
  const date = new Date(dateInput);

  if (Number.isNaN(date.valueOf())) {
    return null;
  }

  return date;
};

const AddBillingTermForm: VoidFunctionComponent = () => {
  const isPerRevisionPlan = window.user?.organization?.stripe_plan === PREMIUM_PER_REVISION_PLAN_NAME;
  const isBinaryDecompEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  const { mutate: addBillingTerm, isPending, error } = useAddBillingTerm();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [revisionLimit, setRevisionLimit] = useState<number>(0);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [type, setType] = useState<SelectOption<BillingTermType>>(
    // This card only shows if the org is on the per revision plan OR has binary decomp enabled.
    // Default to revision is per revision plan is enabled, otherwise default to binary.
    isPerRevisionPlan ? revisionOption : binaryOption,
  );

  const onSubmit = () => {
    const submitErrors = [];
    const termStart = getAndValidateDate(startDate);
    const termEnd = getAndValidateDate(endDate);

    // Validate
    if (!termStart) {
      submitErrors.push('Invalid start date');
    }

    if (!termEnd) {
      submitErrors.push('Invalid end date');
    }

    if (revisionLimit <= 0) {
      submitErrors.push('Revision limit must be greater than 0');
    }

    if (termStart && termEnd && termStart > termEnd) {
      submitErrors.push('Start date cannot be after end date');
    }

    setValidationErrors(submitErrors);
    if (submitErrors.length > 0) {
      return;
    }

    addBillingTerm(
      {
        // These are validated above, so we can safely cast to Date
        termStart: termStart as Date,
        termEnd: termEnd as Date,
        quantity: revisionLimit,
        type: type.value,
      },
      {
        onSuccess: () => {
          setStartDate('');
          setEndDate('');
          setRevisionLimit(0);
          setValidationErrors([]);
        },
      },
    );
  };

  const displayErrors = [...validationErrors];
  if (error) {
    displayErrors.push(errorMessageFromResponse(error) || 'Failed to add billing term');
  }

  return (
    <div className={cx(styles.addTerm)}>
      <Text.H4>Add a new Billing Term</Text.H4>
      {displayErrors.length > 0 && displayErrors.map((err) => <div key={err} className="alert alert-error">{err}</div>)}
      <div className={cx(styles.dateInput)}>
        <LabeledTextInput
          labelVariant="legacy"
          label="Start Date"
          placeholder="Start Date"
          // @ts-expect-error -- "date" is in fact a valid type for a text input
          type="date"
          value={startDate}
          onChange={setStartDate}
          size="small"
        />
        <span />
        <LabeledTextInput
          labelVariant="legacy"
          label="End Date"
          placeholder="End Date"
          // @ts-expect-error -- "date" is in fact a valid type for a text input
          type="date"
          value={endDate}
          onChange={setEndDate}
          size="small"
        />
      </div>
      <div className={cx(styles.quantityInput)}>
        <LabeledNumberInput
          labelVariant="legacy"
          label={`${_.capitalize(type.value)} limit`}
          value={revisionLimit}
          min={1}
          onChange={(val) => setRevisionLimit(parseInt(val, 10) || 0)}
          size="small"
        />
        {isPerRevisionPlan && isBinaryDecompEnabled && (
          <LabeledSelect
            variant="legacy"
            id="billing-term-type"
            label="Billing Term Type"
            options={options}
            value={type}
            onChange={(val) => setType(val as SelectOption<BillingTermType>)}
          />
        )}
        <LoaderButton
          type={BUTTON_TYPE.PRIMARY}
          size={BUTTON_SIZE.LARGE}
          title="Add term"
          isLoading={isPending}
          isDisabled={isPending}
          onClick={onSubmit}
        >
          Add Term
        </LoaderButton>
      </div>
    </div>
  );
};

export default AddBillingTermForm;
